class PredictiveSearch {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('[data-predictive-input]');
    this.results = form.querySelector('[data-predictive-results]');
    this.endpoint = form.dataset.predictiveUrl;
    this.cache = new Map();
    this.controller = null;
    this.timer = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);

    if (!this.input || !this.results || !this.endpoint) return;

    this.input.addEventListener('input', () => this.queueSearch());
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2 && this.results.hasChildNodes()) {
        this.open();
      }
    });
    this.form.addEventListener('keydown', (event) => this.onKeydown(event));
    this.form.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.form.contains(document.activeElement)) this.close();
      });
    });
    this.form.addEventListener('submit', () => this.close());
    document.addEventListener('click', this.onDocumentClick);
  }

  queueSearch() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.search(), 250);
  }

  async search() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.controller?.abort();
      this.results.replaceChildren();
      this.close();
      return;
    }

    const cacheKey = term.toLocaleLowerCase();
    this.controller?.abort();
    if (this.cache.has(cacheKey)) {
      this.results.innerHTML = this.cache.get(cacheKey);
      this.open();
      return;
    }

    const controller = new AbortController();
    this.controller = controller;
    this.input.setAttribute('aria-busy', 'true');
    this.showMessage(this.form.dataset.loadingText, 'predictive-search__loading');
    this.open();

    try {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set(
        'resources[type]',
        'query,product,collection,page,article'
      );
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('section_id', 'predictive-search');

      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error();

      const text = await response.text();
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const section = documentFragment.querySelector(
        '#shopify-section-predictive-search'
      );
      if (!section) throw new Error();
      if (term !== this.input.value.trim()) return;

      const markup = section.innerHTML;
      this.cache.set(cacheKey, markup);
      this.results.innerHTML = markup;
      this.open();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showMessage(this.form.dataset.errorText, 'predictive-search__error');
        this.open();
      }
    } finally {
      if (this.controller === controller) {
        this.input.removeAttribute('aria-busy');
      }
    }
  }

  showMessage(message, className) {
    const status = document.createElement('p');
    status.className = className;
    status.setAttribute('role', 'status');
    status.textContent = message;
    this.results.replaceChildren(status);
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  onKeydown(event) {
    if (this.results.hidden) return;

    const links = [...this.results.querySelectorAll('[data-predictive-link]')];
    const currentIndex = links.indexOf(document.activeElement);

    if (event.key === 'ArrowDown' && links.length) {
      event.preventDefault();
      links[currentIndex < links.length - 1 ? currentIndex + 1 : 0].focus();
    } else if (event.key === 'ArrowUp' && links.length) {
      event.preventDefault();
      if (currentIndex === -1) {
        links[links.length - 1].focus();
      } else if (currentIndex === 0) {
        this.input.focus();
      } else {
        links[currentIndex - 1].focus();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.focus();
    }
  }

  onDocumentClick(event) {
    if (!this.form.contains(event.target)) this.close();
  }

  destroy() {
    window.clearTimeout(this.timer);
    this.controller?.abort();
    document.removeEventListener('click', this.onDocumentClick);
  }
}

const initializePredictiveSearch = (scope = document) => {
  scope
    .querySelectorAll('[data-predictive-search]:not([data-predictive-initialized])')
    .forEach((form) => {
      form.dataset.predictiveInitialized = 'true';
      form.predictiveSearchInstance = new PredictiveSearch(form);
    });
};

initializePredictiveSearch();

document.addEventListener('shopify:section:load', (event) => {
  initializePredictiveSearch(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-predictive-search]').forEach((form) => {
    form.predictiveSearchInstance?.destroy();
  });
});
